import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Shield, AlertTriangle, Check, X, FileText, ExternalLink, Lock } from 'lucide-react';
import { useContent } from '../../lib/content';
import { api, pickLocale } from '../../lib/api';
import { ENFORCEMENT_DATE, AI_ACT_ART_50_URL } from '../../lib/compliance';

/**
 * ComplianceShield — "El examen del regulador" section.
 *
 * Tone (per user pick 1.b): firm, fact-based, NO competitor names. Cites only
 * AI Act art. 50, FTC, California SB 942, NY synthetic-performers law.
 *
 * Anatomy:
 *   1. Eyebrow + countdown to 2 Aug 2026 (AI Act art. 50 enforcement).
 *   2. Bold headline + €35M / 7% turnover penalty callout.
 *   3. Three-column compliance gap table (generic AI · current SaaS · MODARIA).
 *   4. Compliance Report mockup (live from /api/compliance/sample-report).
 *   5. CTA — pídenos un audit gratuito de tu pipeline.
 *
 * Design language: editorial, dark, acid-yellow accent, mono labels,
 * sharp borders, no rounded corners on the report card.
 */

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const ms = target.getTime() - now.getTime();
  const days = Math.max(0, Math.floor(ms / 86_400_000));
  const hours = Math.max(0, Math.floor((ms % 86_400_000) / 3_600_000));
  return { days, hours, expired: ms <= 0 };
}

function Cell({ value }) {
  if (value === 'yes') return <Check size={18} className="text-acid mx-auto" data-testid="cell-yes" />;
  if (value === 'no') return <X size={18} className="text-ink-dim mx-auto" data-testid="cell-no" />;
  if (value === 'partial') return <span className="text-ink-muted text-xs font-mono">⚠</span>;
  return <span className="text-ink-dim text-xs">—</span>;
}

export default function ComplianceShield() {
  const { content, locale } = useContent();
  const data = content.compliance_shield || {};
  const { days, hours } = useCountdown(ENFORCEMENT_DATE);
  const [report, setReport] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/compliance/sample-report')
      .then(({ data: d }) => { if (alive) setReport(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const t = (k, fallback = '') => pickLocale(data[k], locale, fallback);

  const rows = data.rows || [];
  const checks = report?.compliance_checks || {};

  return (
    <section
      id="compliance"
      data-testid="compliance-shield"
      className="relative section bg-bg overflow-hidden"
    >
      {/* Vertical guide line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-line/40 pointer-events-none hidden lg:block" />

      <div className="container-x relative">
        {/* Eyebrow + countdown */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 md:mb-14">
          <div className="flex items-center gap-3">
            <Shield size={14} className="text-acid" />
            <span className="text-eyebrow text-acid">{t('eyebrow', 'EL EXAMEN DEL REGULADOR')}</span>
          </div>
          <div
            data-testid="compliance-countdown"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-muted flex items-center gap-3 border border-line-strong px-4 py-2"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-acid animate-pulse" />
            <span>
              {locale === 'es'
                ? <>LEY EUROPEA DE IA · ART. 50 · ENTRA EN VIGOR EN <strong className="text-acid">{days}D {hours}H</strong></>
                : <>EU AI ACT · ART. 50 · ENFORCEMENT IN <strong className="text-acid">{days}D {hours}H</strong></>}
            </span>
          </div>
        </div>

        {/* Headline + penalty callout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <h2
              data-testid="compliance-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.98] text-ink"
            >
              {t('title', 'Si mañana entra el regulador, ¿puedes demostrar cómo se generó cada imagen de tu catálogo?')}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed mb-8">
              {t('lead', 'A partir del 2 de agosto de 2026, cualquier marca que use IA generativa en su tienda online dentro de la Unión Europea debe poder demostrar el origen, la licencia y la aprobación humana de cada imagen publicada. Los generadores de hoy no están diseñados para responder a esa pregunta.')}
            </p>
            <div className="border-l-2 border-acid pl-5 py-2">
              <div className="text-eyebrow text-acid mb-1">{t('penalty_label', 'Sanción aplicable')}</div>
              <div className="font-display text-3xl md:text-4xl text-ink leading-none">
                {t('penalty_value', '35 millones € o 7%')}
              </div>
              <div className="text-ink-muted text-sm mt-1">
                {t('penalty_caption', 'de la facturación global anual · lo que sea mayor')}
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Gap Table */}
        <div className="mb-16 md:mb-24" data-testid="compliance-gap-table">
          <div className="text-eyebrow text-ink-muted mb-5">{t('table_label', 'LO QUE EL REGULADOR EXIGE')}</div>
          <div className="overflow-x-auto -mx-5 md:mx-0">
            <table className="min-w-[860px] w-full border-t border-line">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left py-5 px-3 text-eyebrow text-ink-muted font-normal w-[42%]">
                    {t('col_obligation', 'Obligación legal')}
                  </th>
                  <th className="py-5 px-3 text-eyebrow text-ink-muted font-normal text-center">
                    {t('col_generic', 'IA genérica')}
                  </th>
                  <th className="py-5 px-3 text-eyebrow text-ink-muted font-normal text-center">
                    {t('col_saas', 'Servicios actuales para moda')}
                  </th>
                  <th className="py-5 px-3 text-acid font-mono uppercase tracking-[0.18em] text-[11px] text-center">
                    MODARIA
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={i}
                    data-testid={`compliance-row-${i}`}
                    className="border-b border-line/60 hover:bg-bg-secondary/40 transition-colors"
                  >
                    <td className="py-5 px-3 text-ink text-sm md:text-base">
                      <div className="font-medium">{pickLocale(row.label, locale)}</div>
                      {row.ref && (
                        <div className="text-eyebrow text-ink-dim mt-1">{row.ref}</div>
                      )}
                    </td>
                    <td className="py-5 px-3 text-center"><Cell value={row.generic} /></td>
                    <td className="py-5 px-3 text-center"><Cell value={row.saas} /></td>
                    <td className="py-5 px-3 text-center bg-acid/5"><Cell value={row.modaria} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-ink-dim font-mono text-[10px] uppercase tracking-[0.22em] mt-4">
            {t('table_footnote', 'Fuentes: Ley Europea de IA (Reglamento 2024/1689) · Sección 5 de la FTC · Ley California SB 942 · Ley NY de actores sintéticos · Estándar de origen digital C2PA')}
          </p>
        </div>

        {/* Compliance Report mockup */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-eyebrow text-ink-muted mb-4">{t('report_label', 'INFORME DE CUMPLIMIENTO · ENTREGABLE')}</div>
            <h3 className="font-display text-3xl md:text-5xl leading-tight text-ink mb-6">
              {t('report_title', 'Cada imagen y vídeo viene con su carpeta lista para el regulador.')}
            </h3>
            <p className="text-ink-muted text-base leading-relaxed mb-6">
              {t('report_body', 'Motor de IA usado, versión, licencia comercial, instrucción completa, parámetros, imagen de origen, firma digital única, sello de origen permanente, marca de agua permanente, cadena de aprobación humana firmada y aviso visible para el cliente. Todo trazable. Todo auditable. Todo en un PDF a un clic.')}
            </p>
            <ul className="space-y-3">
              {(data.report_bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-ink text-sm">
                  <Check size={14} className="text-acid mt-1 shrink-0" />
                  <span>{pickLocale(b, locale)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* The actual report card */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
              className="border border-line-strong bg-bg-secondary/40 backdrop-blur-sm"
              data-testid="compliance-report-card"
            >
              {/* Header strip */}
              <div className="flex items-center justify-between border-b border-line-strong px-5 py-3 bg-bg/60">
                <div className="flex items-center gap-2 text-ink">
                  <FileText size={13} className="text-acid" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                    {report?.report_id || 'MOD-2026-04-29-XXXX'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock size={11} className="text-acid" />
                  <span className="text-ink-dim font-mono text-[10px] uppercase tracking-[0.22em]">
                    {locale === 'es' ? 'Firmado · Verificable' : 'Signed · Verifiable'}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="grid grid-cols-2 divide-x divide-line">
                {/* Asset + Model */}
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'IMAGEN' : 'ASSET'}</div>
                    <div className="text-ink font-mono text-xs break-all">{report?.asset?.id || 'imagen_a47c2f'}</div>
                    <div className="text-ink-dim font-mono text-[10px] mt-1 break-all">{locale === 'es' ? 'FIRMA · ' : 'SIGNATURE · '}{report?.asset?.hash_sha256?.slice(0, 32) || '9f1e3b6c8a2d4f5e0c1b9a8d7e6f5c4b'}…</div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'MOTOR DE IA' : 'AI ENGINE'}</div>
                    <div className="text-ink text-sm">
                      <span className="font-medium">{report?.model?.id || 'nano-banana-2'}</span>
                      <span className="text-ink-muted"> · {report?.model?.version || 'pro-2026-03'}</span>
                    </div>
                    <div className="text-ink-dim text-xs mt-0.5">{report?.model?.provider || 'google'}</div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'LICENCIA' : 'LICENCE'}</div>
                    <div className="text-ink text-sm capitalize">{(report?.model?.license_type || (locale === 'es' ? 'comercial' : 'commercial')).replace(/_/g, ' ')}</div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(report?.model?.permitted_uses || (locale === 'es' ? ['ficha de producto', 'publicidad'] : ['product page', 'advertising'])).map((u, i) => (
                        <span key={i} className="text-[9px] uppercase tracking-[0.18em] font-mono px-1.5 py-0.5 border border-line text-ink-muted">
                          {u.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Provenance */}
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'ORIGEN' : 'PROVENANCE'}</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2 text-ink">
                        <Check size={11} className="text-acid" /> {locale === 'es' ? 'Sello digital · ' : 'Digital seal · '}{report?.provenance?.synthid_strength || (locale === 'es' ? 'fuerte' : 'strong')}
                      </div>
                      <div className="flex items-center gap-2 text-ink">
                        <Check size={11} className="text-acid" /> {locale === 'es' ? 'Sello de origen firmado' : 'Origin seal signed'}
                      </div>
                      <div className="flex items-center gap-2 text-ink">
                        <Check size={11} className="text-acid" /> {locale === 'es' ? 'Marca de agua permanente' : 'Permanent watermark'}
                      </div>
                    </div>
                    <div className="text-ink-dim font-mono text-[10px] mt-2 break-all">
                      {report?.provenance?.c2pa_manifest_id?.slice(0, 36) || 'urn:uuid:b8e1c2a3-4d5f-6789-0abc'}…
                    </div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'AVISO AL CLIENTE' : 'DISCLOSURE'}</div>
                    <div className="text-ink text-xs italic font-display leading-tight">
                      "{report?.provenance?.human_readable_disclosure || (locale === 'es' ? 'Imagen creada con asistencia de IA · aprobada por Atelier N' : 'Image created with AI assistance · approved by Atelier N')}"
                    </div>
                  </div>
                </div>
              </div>

              {/* Validators chain */}
              <div className="border-t border-line-strong px-5 py-4">
                <div className="text-eyebrow text-ink-muted mb-3">{locale === 'es' ? 'CADENA DE APROBACIÓN HUMANA' : 'HUMAN APPROVAL CHAIN'}</div>
                <div className="space-y-2">
                  {(report?.human_validations || [
                    { validator: 'Helena Vidal', role: 'creative_director', decision: 'needs_changes' },
                    { validator: 'Marcos Lara', role: 'brand_lead', decision: 'approved' },
                    { validator: 'Anaïs Roy', role: 'legal', decision: 'approved' },
                  ]).map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        {v.decision === 'approved' ? (
                          <Check size={12} className="text-acid" />
                        ) : v.decision === 'needs_changes' ? (
                          <AlertTriangle size={12} className="text-ink-muted" />
                        ) : (
                          <X size={12} className="text-danger" />
                        )}
                        <span className="text-ink">{v.validator}</span>
                        <span className="text-ink-dim font-mono text-[10px] uppercase tracking-[0.18em]">
                          · {v.role.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
                        v.decision === 'approved' ? 'text-acid' : v.decision === 'needs_changes' ? 'text-ink-muted' : 'text-danger'
                      }`}>
                        {v.decision.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compliance checks */}
              <div className="border-t border-line-strong px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(checks).slice(0, 8).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5">
                    {v ? <Check size={11} className="text-acid shrink-0" /> : <X size={11} className="text-danger shrink-0" />}
                    <span className="text-[9px] uppercase tracking-[0.18em] font-mono text-ink-muted leading-tight">
                      {k.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="border-t border-line-strong px-5 py-3 flex items-center justify-between bg-bg/40">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim">
                  {report?.footer?.issuer || 'MODARIA · A studio by Odnos Lab'}
                </span>
                <a
                  href={report?.footer?.verifiable_at || '#'}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-acid flex items-center gap-1 hover:underline"
                  data-testid="report-verify-link"
                >
                  {locale === 'es' ? 'VERIFICAR' : 'VERIFY'} <ExternalLink size={10} />
                </a>
              </div>
            </motion.div>
            {report?.is_demo && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim leading-snug" data-testid="report-demo-note">
                {locale === 'es'
                  ? 'INFORME DE EJEMPLO · TU SISTEMA EN CASA EMITIRÁ LA CARPETA REAL CON TUS DATOS Y TU PROCESO.'
                  : 'DEMO REPORT · YOUR IN-HOUSE SYSTEM WILL EMIT THE REAL DOSSIER WITH YOUR DATA AND PROCESS.'}
              </p>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 md:mt-20 border-t border-line pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-ink text-base md:text-lg max-w-xl leading-relaxed">
            {t('cta_text', '¿Tu proceso de IA actual aguanta una auditoría? Te lo decimos en 30 minutos, gratis y por escrito.')}
          </p>
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="compliance-cta-audit"
              className="btn-acid magnetic !text-[11px]"
            >
              {t('cta_primary', 'Pídenos una revisión gratuita')}
            </a>
            <a
              href={AI_ACT_ART_50_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost magnetic !text-[11px]"
              data-testid="compliance-cta-act"
            >
              {t('cta_secondary', 'Leer la Ley Europea de IA · Art. 50')}
            </a>
          </div>
        </div>

        {/* "What about training data?" — answer the obvious objection */}
        <div className="mt-20 md:mt-24 border-t border-line pt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12" data-testid="compliance-training-block">
          <div className="lg:col-span-4">
            <div className="text-eyebrow text-ink-muted mb-4">
              {locale === 'es' ? 'PREGUNTA OBLIGADA' : 'THE OBVIOUS QUESTION'}
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">
              {locale === 'es'
                ? '¿Cómo usar modelos IA sin saber con qué se entrenaron?'
                : 'How do you use AI models without knowing what they were trained on?'}
            </h3>
          </div>
          <div className="lg:col-span-8 space-y-5 text-ink-muted text-base leading-relaxed">
            <p>
              {locale === 'es'
                ? 'No los entrenamos nosotros. MODARIA es quien USA los motores de IA, no quien los crea. Usamos 9 motores comerciales bajo la licencia que publica cada fabricante (OpenAI, Google, Anthropic, Black Forest Labs, ByteDance, Kuaishou, Midjourney). La Ley Europea de IA (Art. 50 §1-§2) hace responsable al fabricante de los datos con los que entrenó el modelo; quien lo usa es responsable del buen uso. Esa frontera es la que dejamos registrada.'
                : 'We don\'t train them. MODARIA USES the AI engines — we don\'t build them. We use 9 commercial engines under the licence published by each maker (OpenAI, Google, Anthropic, Black Forest Labs, ByteDance, Kuaishou, Midjourney). The EU AI Act (Art. 50 §1-§2) makes the maker liable for what the model was trained on; whoever uses it is liable for correct use. That boundary is what we log.'}
            </p>
            <ul className="space-y-2.5 mt-2">
              {[
                {
                  es: 'Cada generación queda registrada con motor + versión + fabricante + licencia comercial activa en ese momento + enlace a las condiciones de servicio del fabricante.',
                  en: 'Every generation is logged with engine + version + maker + commercial licence active at that moment + link to the maker\'s terms of service.',
                },
                {
                  es: 'MODARIA bloquea por defecto los usos que el fabricante prohíbe (famosos, menores, contenido político, contenido para adultos…).',
                  en: 'MODARIA blocks by default any use forbidden by the maker (celebrities, minors, political content, adult content…).',
                },
                {
                  es: 'No quitamos los sellos digitales ni los avisos de origen que el fabricante deja en la imagen (sería ilegal según el Art. 50).',
                  en: 'We do NOT strip the digital seals or origin notices the maker injects in the image (would breach Art. 50).',
                },
                {
                  es: 'Si surge un pleito contra un fabricante (ej.: NYTimes vs OpenAI, Getty vs Stability…), MODARIA avisa a tu marca y te ofrece cambiar a otro motor registrado mientras se resuelve.',
                  en: 'If a lawsuit emerges against a maker (e.g. NYTimes vs OpenAI, Getty vs Stability…), MODARIA notifies your brand and offers to switch to another registered engine while it\'s resolved.',
                },
                {
                  es: 'Tu marca firma una vez las condiciones de cada fabricante que actives (igual que ya haces con Adobe o con tu biblioteca de imágenes); MODARIA guarda una copia firmada digitalmente en tu sistema.',
                  en: 'Your brand signs each maker\'s terms once when activated (same as Adobe or your image library today); MODARIA keeps a digitally signed copy on your system.',
                },
                {
                  es: 'MODARIA NO entrena motores con tus datos. Tus prendas, tus modelos reales y tus instrucciones se quedan dentro de tu casa.',
                  en: 'MODARIA does NOT train engines on your data. Your garments, real models and instructions stay inside your house.',
                },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-ink text-sm md:text-base" data-testid={`compliance-training-bullet-${i}`}>
                  <Check size={14} className="text-acid mt-1 shrink-0" />
                  <span>{pickLocale(b, locale)}</span>
                </li>
              ))}
            </ul>
            <p className="text-ink-dim font-mono text-[10px] uppercase tracking-[0.18em] mt-5">
              {locale === 'es'
                ? 'LEY EUROPEA DE IA (REG. 2024/1689) · ART. 50 §1-§2 · ART. 53 OBLIGACIONES DE LOS FABRICANTES DE MOTORES DE IA DE PROPÓSITO GENERAL'
                : 'EU AI ACT (REG. 2024/1689) · ART. 50 §1-§2 · ART. 53 OBLIGATIONS OF GENERAL-PURPOSE AI MODEL PROVIDERS'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
